import { ApiError, ApiErrors } from "../types/error.types";
import type { AuthResponse } from "../types/model.types";
import { VenuesEndpoint } from "./endpoint";

export enum Environment {
  PRODUCTION = "PRODUCTION",
  STAGING = "STAGING",
  DEVELOPMENT = "DEVELOPMENT",
}

export interface ResourceIdentifier {
  environment: Environment;
  path: string;
  method: string;
  queryParams: Record<string, string>;
  body?: string;
  hash: string;
}

// Helper function to validate and parse ResourceIdentifier from JSON
export function parseResourceIdentifier(json: any): ResourceIdentifier {
  if (!json || typeof json !== "object") {
    throw new Error("Invalid ResourceIdentifier: must be an object");
  }

  const requiredFields = [
    "environment",
    "path",
    "method",
    "queryParams",
    "hash",
  ];
  for (const field of requiredFields) {
    if (!(field in json)) {
      throw new Error(
        `Invalid ResourceIdentifier: missing required field '${field}'`,
      );
    }
  }

  // Validate environment
  if (!Object.values(Environment).includes(json.environment)) {
    throw new Error(
      `Invalid ResourceIdentifier: invalid environment '${json.environment}'`,
    );
  }

  // Validate method
  if (
    typeof json.method !== "string" ||
    !["GET", "POST", "PUT", "DELETE", "PATCH"].includes(
      json.method.toUpperCase(),
    )
  ) {
    throw new Error(
      `Invalid ResourceIdentifier: invalid method '${json.method}'`,
    );
  }

  // Validate path
  if (typeof json.path !== "string" || json.path.length === 0) {
    throw new Error(
      "Invalid ResourceIdentifier: path must be a non-empty string",
    );
  }

  // Validate queryParams
  if (!json.queryParams || typeof json.queryParams !== "object") {
    throw new Error(
      "Invalid ResourceIdentifier: queryParams must be an object",
    );
  }

  // Validate hash
  if (typeof json.hash !== "string") {
    throw new Error("Invalid ResourceIdentifier: hash must be a string");
  }

  const resourceIdentifier: ResourceIdentifier = {
    environment: json.environment,
    path: json.path,
    method: json.method,
    queryParams: json.queryParams,
    body: json.body,
    hash: json.hash,
  };

  return resourceIdentifier;
}

export interface ApiConfig {
  defaultEnvironment: Environment | null;
  credentials: { access: string; secret: string };
  userAgent: string;
  integratorName: string;
  sdkVersion: string;
  uuid: string;
  clientVersion?: string;
  authHost?: string;
}

// Declare the native interface for API responses
declare const ApiNativeBridge: {
  onApiSuccess: (resourceIdentifier: string, response: string) => void;
  onApiError: (resourceIdentifier: string, error: string) => void;
};

export class Api {
  readonly config: ApiConfig;
  private authToken: string | null = null;
  private authPromise: Promise<string> | null = null;
  private baseUrls: Record<Environment, string> = {
    [Environment.PRODUCTION]: "https://api.mixhalo.rocks/v1",
    [Environment.STAGING]: "https://api-staging.mixhalo.rocks/v1",
    [Environment.DEVELOPMENT]: "https://api-dev.mixhalo.rocks/v1",
  };

  constructor(config: ApiConfig) {
    this.config = config;
  }

  private notifyNativeSuccess(
    resourceIdentifier: ResourceIdentifier,
    response: any,
  ): void {
    if (
      typeof ApiNativeBridge !== "undefined" &&
      typeof ApiNativeBridge.onApiSuccess === "function"
    ) {
      try {
        // WORKAROUND: The native (Android) SDK re-serializes stored objects and
        // embeds them in a single-quoted JS string literal when calling back into
        // the player (e.g. `play('<mixJson>')`). A raw ASCII apostrophe U+0027 in
        // any field (e.g. a session title like "Vik's Keynote") terminates that
        // literal early, producing a JS parse error and a "null" result that fails
        // to decode as PlayMixResult. JSON does not escape apostrophes, so we swap
        // them for the typographically near-identical U+2019 here, on the native
        // bridge path only, so the char never enters the data Android round-trips.
        // Structural JSON syntax never uses ', so replacing it only touches string
        // values. Remove once the native SDK escapes bridge string literals.
        ApiNativeBridge.onApiSuccess(
          JSON.stringify(resourceIdentifier),
          JSON.stringify(response).replace(/'/g, "’"),
        );
      } catch (error) {
        console.error("Failed to notify native layer of API success:", error);
      }
    }
  }

  private notifyNativeError(
    resourceIdentifier: ResourceIdentifier,
    error: ApiError,
  ): void {
    if (
      typeof ApiNativeBridge !== "undefined" &&
      typeof ApiNativeBridge.onApiError === "function"
    ) {
      try {
        const errorPayload = {
          message: error.message,
          status: error.data?.statusCode,
          timestamp: new Date().toISOString(),
        };
        ApiNativeBridge.onApiError(
          JSON.stringify(resourceIdentifier),
          JSON.stringify(errorPayload),
        );
      } catch (e) {
        console.error("Failed to notify native layer of API error:", e);
      }
    }
  }

  private getBaseUrl(environment: Environment): string {
    return this.baseUrls[environment];
  }

  private getAuthBaseUrl(environment: Environment): string {
    // Use authHost override if available for auth requests only
    if (this.config.authHost !== undefined) {
      // If authHost is empty string, return empty string to remove host and keep path only
      if (this.config.authHost === "") {
        return "";
      }
      return this.config.authHost;
    }
    return this.baseUrls[environment];
  }

  private authenticate(environment: Environment): Promise<string> {
    if (this.authPromise) {
      return this.authPromise;
    }

    this.authPromise = (async () => {
      try {
        const resourceIdentifier: ResourceIdentifier = {
          environment,
          path: "/login",
          method: "POST",
          queryParams: {},
          body: JSON.stringify({
            accessKey: this.config.credentials.access,
            secretKey: this.config.credentials.secret,
          }),
          hash: "",
        };

        const response = await this.internalRequest<AuthResponse>(
          resourceIdentifier,
          true,
        );

        if (!response.token) {
          throw ApiErrors.InvalidResponse;
        }
        this.authToken = response.token;
        return this.authToken!;
      } catch (error) {
        this.authToken = null;
        this.authPromise = null;
        throw error;
      }
    })();

    return this.authPromise;
  }

  public get venues(): VenuesEndpoint {
    return new VenuesEndpoint(this);
  }

  /**
   * Makes a POST request to any URL with authentication
   * Useful for GraphQL endpoints or other services that need auth
   * @param url - Full URL to make the request to
   * @param body - Request body (will be JSON stringified)
   * @returns Parsed JSON response
   */
  public async post<T>(url: string, body: any): Promise<T> {
    // Ensure we're authenticated
    const environment =
      this.config.defaultEnvironment || Environment.PRODUCTION;
    if (!this.authToken) {
      await this.authenticate(environment);
    }

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "MHClient-User-Agent": this.config.userAgent,
        "MHClient-Product": this.config.integratorName,
        "MHClient-SDK-Version": this.config.sdkVersion,
        "MHClient-UUID": this.config.uuid,
        ...(this.config.clientVersion
          ? { "MHClient-Version": this.config.clientVersion }
          : {}),
        ...(this.authToken
          ? { Authorization: `Bearer ${this.authToken}` }
          : {}),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `Request failed: ${response.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorMessage;
      } catch {
        // Use default error message
      }
      throw ApiErrors.HttpError(response.status, errorMessage);
    }

    const responseText = await response.text();

    if (response.status === 204 || !responseText) {
      return {} as T;
    }

    try {
      return JSON.parse(responseText) as T;
    } catch (e) {
      throw ApiErrors.InvalidResponse;
    }
  }

  public async request<T>(resourceIdentifier: ResourceIdentifier): Promise<T> {
    try {
      const result = await this.internalRequest<T>(resourceIdentifier, false);
      this.notifyNativeSuccess(resourceIdentifier, result);
      return result;
    } catch (error) {
      if (error instanceof ApiError) {
        this.notifyNativeError(resourceIdentifier, error);
      }
      throw error;
    }
  }

  /**
   * Non-async version of request that calls the native bridge but returns void.
   * The response will be handled through the native bridge callbacks.
   * @param resourceIdentifier The resource identifier for the API request
   */
  public nativeRequest(resourceIdentifierString: string): void {
    // Parse and validate the resourceIdentifier if it's not already a ResourceIdentifier
    let resourceIdentifier = parseResourceIdentifier(resourceIdentifierString);
    // Fire and forget - start the request but don't wait for it
    this.internalRequest(resourceIdentifier, false)
      .then((result) => {
        this.notifyNativeSuccess(resourceIdentifier, result);
      })
      .catch((error) => {
        if (error instanceof ApiError) {
          this.notifyNativeError(resourceIdentifier, error);
        } else {
          // Convert unknown errors to SDKError
          const apiError = ApiErrors.Unknown(
            error.message || "Unknown error occurred",
          );
          this.notifyNativeError(resourceIdentifier, apiError);
        }
      });
  }

  private buildRequest(
    resourceIdentifier: ResourceIdentifier,
    isAuthRequest = false,
  ): { url: URL; requestInit: RequestInit } {
    // Use proxy paths for all environments (Vite will handle the routing)
    const baseUrl = isAuthRequest
      ? this.getAuthBaseUrl(resourceIdentifier.environment)
      : this.getBaseUrl(resourceIdentifier.environment);

    // Construct URL by combining the proxy base path with the resource path
    const fullPath = `${baseUrl}${resourceIdentifier.path.startsWith("/") ? resourceIdentifier.path : `/${resourceIdentifier.path}`}`;
    // const url = new URL(fullPath);
    const url = new URL(fullPath, window.location.origin);

    if (resourceIdentifier.queryParams) {
      Object.entries(resourceIdentifier.queryParams).forEach(([key, value]) => {
        url.searchParams.append(key, value as string);
      });
    }

    const headers: Record<string, string> = {
      Accept: "application/json",
      "Content-Type": "application/json",
      "MHClient-User-Agent": this.config.userAgent,
      "MHClient-Product": this.config.integratorName,
      "MHClient-SDK-Version": "999999.0.0", ///this.config.sdkVersion,
      "MHClient-UUID": this.config.uuid,
    };

    if (this.config.clientVersion) {
      headers["MHClient-Version"] = this.config.clientVersion;
    }

    if (this.authToken) {
      headers["Authorization"] = `Bearer ${this.authToken}`;
    }

    const requestInit: RequestInit = {
      method: resourceIdentifier.method || "GET",
      headers: new Headers(headers),
    };

    if (resourceIdentifier.body) {
      requestInit.body = resourceIdentifier.body;
    }

    return { url, requestInit };
  }

  private async internalRequest<T>(
    resourceIdentifier: ResourceIdentifier,
    isAuthRequest = false,
  ): Promise<T> {
    if (!this.authToken && !isAuthRequest) {
      await this.authenticate(resourceIdentifier.environment);
    }

    // Check network connectivity
    if (!navigator.onLine) {
      throw ApiErrors.HttpError(
        0,
        "Network is offline. Please check your internet connection.",
      );
    }

    let { url, requestInit } = this.buildRequest(
      resourceIdentifier,
      isAuthRequest,
    );

    let response: Response;
    try {
      response = await fetch(url.toString(), requestInit);
    } catch (e: any) {
      // Provide more specific error messages based on error type
      let detailedMessage = e.message;
      if (e.name === "TypeError" && e.message.includes("fetch")) {
        detailedMessage = `Network error: ${e.message}. This usually indicates a CORS issue, network connectivity problem, or invalid URL.`;
      } else if (e.message.includes("CORS")) {
        detailedMessage = `CORS error: ${e.message}. The server may not be configured to allow requests from this origin.`;
      } else if (e.message.includes("Failed to fetch")) {
        detailedMessage = `Fetch failed: ${e.message}. Possible causes: network connectivity, DNS resolution, or server unreachable. URL attempted: ${url.toString()}`;
      }

      throw ApiErrors.HttpError(0, detailedMessage);
    }

    if (response.status === 403 && !isAuthRequest) {
      this.authToken = null;
      this.authPromise = null;
      await this.authenticate(resourceIdentifier.environment);

      // Rebuild request with new auth token
      const { url: retryUrl, requestInit: retryRequestInit } =
        this.buildRequest(resourceIdentifier, isAuthRequest);
      url = retryUrl;
      requestInit = retryRequestInit;

      response = await fetch(url.toString(), requestInit);
    }

    const responseBodyText = await response.text();

    if (!response.ok) {
      let errorBody;
      try {
        errorBody = JSON.parse(responseBodyText);
      } catch (e) {
        errorBody = { message: response.statusText };
      }
      throw ApiErrors.HttpError(
        response.status,
        errorBody.message || "Request failed",
      );
    }

    if (response.status === 204 || !responseBodyText) {
      return {} as T;
    }

    try {
      const parsedResponse = JSON.parse(responseBodyText);

      // NOTE: We intentionally no longer stamp `rawJson` (the full response
      // body) onto the EventMedia object or its streams. Doing so duplicated
      // the entire payload once per stream, so a large /event_media response
      // was inflated by a factor of (stream count + 1) before being
      // JSON.stringify'd across the native bridge. That overflowed Android's
      // JavascriptInterface Binder transaction limit and crashed the app.
      // Nothing (js-sdk or the native SDKs, which use ignoreUnknownKeys)
      // actually reads `rawJson`, so removing it is behavior-preserving.

      return parsedResponse;
    } catch (e) {
      throw ApiErrors.InvalidResponse;
    }
  }
}

export let api: Api | null = null;

export function createApi(config: ApiConfig): Api {
  if (!api) {
    api = new Api(config);
  }
  return api;
}

export function getApi(): Api | null {
  return api;
}
